/**
 * "Itens previstos da manutenção": o checklist de execução da O.S. preventiva, agrupado por
 * sistema (Motor, Freios…) com ícone, itens em duas colunas e o ✓ de cada serviço.
 *
 * É controle de EXECUÇÃO — não inspeciona condição e não abre ocorrência. Os itens são o
 * snapshot do modelo no momento em que a O.S. foi aberta. Item técnico mostra os selos do
 * resultado e abre a execução técnica (cartões, eixos ou painel de posições) em tela cheia.
 */
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { MaintenanceWorkOrderChecklistItemDto, MaintenanceWorkOrderDetailDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { CheckMark } from "./CheckMark";
import { MaintenanceIcon, sectionIconName } from "./MaintenanceIcons";
import { COND_STYLE } from "./work-order-inspection-ui";
import {
  CHECK_LABEL, FILTERS, NOT_DONE_REASONS, groupBySection, matchesFilter, reasonText, type ChecklistFilter,
} from "./work-order-checklist-ui";

type Painel = { id: string; modo: "not_done" | "not_applicable" | "note" };

export function WorkOrderChecklist({
  o, editavel, podeInspecionar = false, tecnicoAberto, onTecnico, onMudou,
}: {
  o: MaintenanceWorkOrderDetailDto; editavel: boolean; podeInspecionar?: boolean;
  tecnicoAberto: string | null; onTecnico: (id: string | null) => void; onMudou: () => void;
}) {
  const { items, summary, bound } = o.checklist;
  const [filtro, setFiltro] = useState<ChecklistFilter>("all");
  const [fechadas, setFechadas] = useState<Set<string>>(new Set());
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const secoes = useMemo(
    () => groupBySection(items).map((s) => ({ ...s, visiveis: s.items.filter((i) => matchesFilter(i, filtro)) })).filter((s) => s.visiveis.length),
    [items, filtro],
  );

  const marcar = useMutation({
    mutationFn: (v: { item: MaintenanceWorkOrderChecklistItemDto; status: string; reason?: string | null; notes?: string | null }) =>
      api.patch(`/maintenance/work-orders/${o.id}/checklist-items/${v.item.id}`, { status: v.status, reason: v.reason ?? null, notes: v.notes ?? null }),
    onSuccess: () => { setErro(null); setPainel(null); onMudou(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível marcar o item."),
  });

  if (!items.length) {
    // Só preventiva mostra o aviso; corretiva não tem checklist de execução.
    if (o.type !== "preventive") return null;
    return (
      <section className="card p-4">
        <h3 className="font-medium text-gray-900">Itens previstos da manutenção</h3>
        <p className="mt-1 text-sm text-gray-500">
          {o.planComponents.length > 0 && !bound
            ? "Nenhum checklist vinculado para este plano/veículo."
            : "Checklist preventivo não registrado nesta O.S."}
        </p>
      </section>
    );
  }

  return (
    <section className="card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tm-50 text-tm-700"><MaintenanceIcon name="wrench" className="h-6 w-6" /></span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Itens previstos da manutenção</h3>
            <p className="text-sm text-gray-500">Itens validados automaticamente conforme o checklist vinculado ao grupo do veículo.</p>
          </div>
        </div>
        <span className="rounded-full bg-tm-50 px-3 py-1 text-xs font-medium text-tm-700" aria-label="Progresso">
          {summary.done} de {summary.total} itens {summary.done === 1 ? "realizado" : "realizados"} · {summary.progressPct}%
        </span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-valuenow={summary.progressPct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-tm-500 transition-all" style={{ width: `${summary.progressPct}%` }} />
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrar itens">
        {FILTERS.map((f) => (
          <button key={f.id} role="tab" aria-selected={filtro === f.id} onClick={() => setFiltro(f.id)}
            className={`rounded-full border px-3 py-1 text-xs ${filtro === f.id ? "border-tm-600 bg-tm-50 text-tm-700 font-medium" : "border-surface-border text-gray-600 hover:bg-surface-muted"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {erro && <p className="rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}
      {secoes.length === 0 && <p className="text-sm text-gray-500">Nenhum item neste filtro.</p>}

      <div className="gap-x-8 xl:columns-2">
        {secoes.map((s) => {
          const recolhida = fechadas.has(s.name);
          return (
            <div key={s.name} className="mb-4 break-inside-avoid">
              <button type="button" aria-expanded={!recolhida}
                onClick={() => setFechadas((x) => { const n = new Set(x); if (n.has(s.name)) n.delete(s.name); else n.add(s.name); return n; })}
                className="mb-1.5 flex w-full items-center justify-between border-b border-gray-100 pb-1.5 text-left">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <MaintenanceIcon name={sectionIconName(s.name)} className="h-5 w-5 text-gray-700" />{s.name}
                </span>
                <span className="text-xs text-gray-500">{s.done} de {s.total} {recolhida ? "▸" : "▾"}</span>
              </button>
              {!recolhida && (
                <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                  {s.visiveis.map((i) => (
                    <li key={i.id} className="py-1">
                      <div className="flex items-start gap-2">
                        <div className="pt-0.5">
                          <CheckMark
                            status={i.status} label={i.itemName}
                            // item técnico vira ✓ pela execução técnica, não pelo clique
                            disabled={!editavel || marcar.isPending || i.executionType !== "simple_check"}
                            onClick={() => marcar.mutate({ item: i, status: i.status === "done" ? "pending" : i.status === "pending" ? "done" : "pending" })}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm leading-snug ${i.status === "done" ? "text-gray-600" : "text-gray-900"}`}>
                            {i.itemName}
                            {i.required && <span className="ml-1.5 rounded bg-gray-100 px-1 text-[10px] uppercase tracking-wide text-gray-500">obrig.</span>}
                          </p>
                          {i.itemDescription && <p className="text-xs text-gray-400">{i.itemDescription}</p>}
                          {i.executionType !== "simple_check" && (
                            <ResumoTecnico t={o.technical?.items[i.id]} aberto={tecnicoAberto === i.id} podeExecutar={podeInspecionar && editavel}
                              onAbrir={() => onTecnico(tecnicoAberto === i.id ? null : i.id)} />
                          )}
                          {i.status !== "pending" && i.status !== "done" && (
                            <p className={`text-xs ${i.status === "not_done" ? "text-danger-600" : "text-gray-500"}`}>
                              {CHECK_LABEL[i.status]}{i.reason ? ` · ${reasonText(i.reason)}` : ""}
                            </p>
                          )}
                          {i.notes && <p className="whitespace-pre-line text-xs text-gray-500">{i.notes}</p>}
                        </div>
                        {editavel && i.executionType === "simple_check" && (
                          <button type="button" className="shrink-0 rounded px-1.5 text-gray-400 hover:bg-surface-muted hover:text-gray-700"
                            aria-label={`Mais opções de ${i.itemName}`} aria-expanded={painel?.id === i.id}
                            onClick={() => setPainel(painel?.id === i.id ? null : { id: i.id, modo: "note" })}>⋯</button>
                        )}
                      </div>
                      {painel?.id === i.id && (
                        <PainelItem item={i} modo={painel.modo} pendente={marcar.isPending}
                          onModo={(modo) => setPainel({ id: i.id, modo })} onFechar={() => setPainel(null)}
                          onSalvar={(status, reason, notes) => marcar.mutate({ item: i, status, reason, notes })} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Selos do item técnico: dá para ver o resultado sem abrir o detalhe. */
function ResumoTecnico({
  t, aberto, podeExecutar, onAbrir,
}: {
  t: NonNullable<MaintenanceWorkOrderDetailDto["technical"]>["items"][string] | undefined;
  aberto: boolean; podeExecutar: boolean; onAbrir: () => void;
}) {
  const iniciado = !!t && t.inspected > 0;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {iniciado && (
        <>
          {t!.ok > 0 && <span className={COND_STYLE.ok.badge}>✓ {t!.ok} {t!.ok === 1 ? "normal" : "normais"}</span>}
          {t!.attention > 0 && <span className={COND_STYLE.attention.badge}>⚠ {t!.attention} atenção</span>}
          {t!.critical > 0 && <span className={COND_STYLE.critical.badge}>✕ {t!.critical} {t!.critical === 1 ? "crítico" : "críticos"}</span>}
          {t!.pending > 0 && <span className="badge-gray">{t!.pending} pendente{t!.pending === 1 ? "" : "s"}</span>}
          {t!.expected != null && t!.inspected < t!.expected && <span className="text-xs text-gray-500">{t!.inspected} de {t!.expected} avaliados</span>}
        </>
      )}
      <button type="button" className="rounded border border-tm-600/40 px-2 py-0.5 text-xs font-medium text-tm-700 hover:bg-tm-50"
        aria-expanded={aberto} onClick={onAbrir}>
        {aberto ? "Fechar" : podeExecutar ? (iniciado ? "Continuar" : "Executar") : "Ver inspeção"}
      </button>
    </div>
  );
}

function PainelItem({
  item, modo, pendente, onModo, onFechar, onSalvar,
}: {
  item: MaintenanceWorkOrderChecklistItemDto;
  modo: Painel["modo"];
  pendente: boolean;
  onModo: (m: Painel["modo"]) => void;
  onFechar: () => void;
  onSalvar: (status: string, reason: string | null, notes: string | null) => void;
}) {
  const [reason, setReason] = useState(item.reason ?? "");
  const [notes, setNotes] = useState(item.notes ?? "");
  const exigeNota = modo === "not_done" && reason === "other";
  const pronto = modo === "not_done" ? !!reason && (!exigeNota || notes.trim() !== "") : true;
  return (
    <div className="ml-7 mt-2 space-y-2 rounded-lg border border-surface-border bg-surface-muted p-2.5">
      <div className="flex flex-wrap gap-1.5">
        {([["note", "Observação"], ["not_done", "Não realizado"], ["not_applicable", "Não se aplica"]] as const).map(([m, rotulo]) => (
          <button key={m} type="button" onClick={() => onModo(m)}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${modo === m ? "border-tm-600 bg-white text-tm-700 font-medium" : "border-surface-border bg-white text-gray-600"}`}>
            {rotulo}
          </button>
        ))}
      </div>
      {modo !== "note" && (
        <select className="input w-full" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo">
          <option value="">{modo === "not_done" ? "Motivo (obrigatório)…" : "Motivo (opcional)…"}</option>
          {NOT_DONE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      )}
      <textarea className="input w-full" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder={exigeNota ? "Descreva o motivo (obrigatório para “Outro”)" : "Observação do item (opcional)"} />
      <div className="flex gap-2">
        <button type="button" className="btn-secondary" onClick={onFechar}>Fechar</button>
        <button type="button" className="btn-primary" disabled={pendente || !pronto}
          onClick={() => onSalvar(modo === "note" ? item.status : modo, modo === "note" ? item.reason : reason || null, notes.trim() || null)}>
          {pendente ? "Salvando…" : modo === "note" ? "Salvar observação" : "Confirmar"}
        </button>
      </div>
    </div>
  );
}
